import { BLOCK_TYPE, NUMBER_TYPE, SIZE } from './constants';
import { makeBlockTile, makeNumberTile, randomTileValue } from './tiles';

export const createGrid = (tiles) => {
  const grid = Array.from({ length: SIZE }, () => Array(SIZE).fill(null));
  tiles.forEach((tile) => {
    grid[tile.row][tile.col] = tile;
  });
  return grid;
};

export const emptyCells = (grid) => {
  const cells = [];
  grid.forEach((row, r) =>
    row.forEach((cell, c) => {
      if (!cell) cells.push([r, c]);
    })
  );
  return cells;
};

export const movesAvailable = (tiles) => {
  const grid = createGrid(tiles);
  if (emptyCells(grid).length) return true;

  for (let r = 0; r < SIZE; r += 1) {
    for (let c = 0; c < SIZE; c += 1) {
      const tile = grid[r][c];
      if (!tile || tile.type === BLOCK_TYPE) continue;
      const right = grid[r]?.[c + 1];
      const down = grid[r + 1]?.[c];
      if (right && right.type === NUMBER_TYPE && right.value === tile.value) return true;
      if (down && down.type === NUMBER_TYPE && down.value === tile.value) return true;
    }
  }
  return false;
};

const lineCoordinates = (direction, index) => {
  const coords = [];
  if (direction === 'left' || direction === 'right') {
    for (let c = 0; c < SIZE; c += 1) {
      coords.push([index, direction === 'left' ? c : SIZE - 1 - c]);
    }
  } else {
    for (let r = 0; r < SIZE; r += 1) {
      coords.push([direction === 'up' ? r : SIZE - 1 - r, index]);
    }
  }
  return coords;
};

const destinationForTarget = (direction, lineIndex, targetIndex) => {
  switch (direction) {
    case 'left':
      return { row: lineIndex, col: targetIndex };
    case 'right':
      return { row: lineIndex, col: SIZE - 1 - targetIndex };
    case 'up':
      return { row: targetIndex, col: lineIndex };
    case 'down':
      return { row: SIZE - 1 - targetIndex, col: lineIndex };
    default:
      return { row: lineIndex, col: targetIndex };
  }
};

export const purgeExpiredBlocks = (tiles, now) =>
  tiles.filter((tile) => tile.type !== BLOCK_TYPE || !tile.expiresAt || tile.expiresAt > now);

export const ensureBlockExpiry = (tile, now) => {
  if (tile.type === BLOCK_TYPE && !tile.expiresAt) {
    return { ...tile, expiresAt: now + 20000 };
  }
  return tile;
};

export const moveTiles = (tiles, direction, nextId) => {
  const grid = createGrid(tiles);
  let moved = false;
  let gained = 0;
  let merges = 0;
  const updatedTiles = [];

  for (let line = 0; line < SIZE; line += 1) {
    const coords = lineCoordinates(direction, line);
    let segmentStart = 0;

    const pushBlockIfPresent = (blockIndex) => {
      if (blockIndex < coords.length) {
        const [br, bc] = coords[blockIndex];
        const blockTile = grid[br][bc];
        if (blockTile) {
          updatedTiles.push({ ...blockTile, isNew: false, justMerged: false });
        }
      }
    };

    while (segmentStart < coords.length) {
      let blockIndex = coords.length;
      for (let idx = segmentStart; idx < coords.length; idx += 1) {
        const [r, c] = coords[idx];
        if (grid[r][c]?.type === BLOCK_TYPE) {
          blockIndex = idx;
          break;
        }
      }

      const segmentCoords = coords.slice(segmentStart, blockIndex);
      const segmentTiles = segmentCoords
        .map(([r, c]) => grid[r][c])
        .filter(Boolean)
        .filter((t) => t.type !== BLOCK_TYPE)
        .map((t) => ({ ...t, isNew: false, justMerged: false }));
      let targetIndex = segmentStart;

      for (let i = 0; i < segmentTiles.length; i += 1) {
        const current = segmentTiles[i];
        const next = segmentTiles[i + 1];
        const destination = destinationForTarget(direction, line, targetIndex);

        if (next && next.value === current.value) {
          const mergedTile = {
            id: nextId(),
            value: current.value * 2,
            row: destination.row,
            col: destination.col,
            type: NUMBER_TYPE,
            justMerged: true,
          };
          updatedTiles.push(mergedTile);
          gained += mergedTile.value;
          merges += 1;
          moved =
            moved ||
            current.row !== destination.row ||
            current.col !== destination.col ||
            next.row !== destination.row ||
            next.col !== destination.col;
          i += 1;
        } else {
          const movedTile = { ...current, row: destination.row, col: destination.col };
          updatedTiles.push(movedTile);
          moved = moved || current.row !== destination.row || current.col !== destination.col;
        }
        targetIndex += 1;
      }

      if (blockIndex >= coords.length) break;
      pushBlockIfPresent(blockIndex);
      segmentStart = blockIndex + 1;
    }
  }

  const maxTile = updatedTiles.reduce((max, tile) => {
    if (tile.type === BLOCK_TYPE) return max;
    return Math.max(max, tile.value);
  }, 0);

  return { tiles: updatedTiles, moved, gained, merges, maxTile };
};

export const addRandomTileToTiles = (tiles, nextId) => {
  const grid = createGrid(tiles);
  const empties = emptyCells(grid);
  if (!empties.length) return tiles;
  const [row, col] = empties[Math.floor(Math.random() * empties.length)];
  return [...tiles, makeNumberTile(randomTileValue(), row, col, nextId)];
};

export const addGarbageTile = (tiles, nextId) => {
  const grid = createGrid(tiles);
  const empties = emptyCells(grid);
  if (!empties.length) return tiles;
  const [row, col] = empties[Math.floor(Math.random() * empties.length)];
  return [...tiles, makeBlockTile(row, col, nextId)];
};

export const clearGarbageTile = (tiles) => {
  const numberTiles = tiles.filter((t) => t.type === NUMBER_TYPE);
  if (!numberTiles.length) return tiles;
  const victim = numberTiles[Math.floor(Math.random() * numberTiles.length)];
  return tiles.filter((t) => t.id !== victim.id);
};

export const shuffleTiles = (tiles) => {
  if (tiles.length < 2) return tiles;
  const numberTiles = tiles.filter((t) => t.type === NUMBER_TYPE);
  const pairs = [];
  for (let i = 0; i < numberTiles.length; i += 1) {
    for (let j = i + 1; j < numberTiles.length; j += 1) {
      if (numberTiles[i].value !== numberTiles[j].value) {
        pairs.push([numberTiles[i], numberTiles[j]]);
      }
    }
  }
  if (!pairs.length) return tiles;
  const [first, second] = pairs[Math.floor(Math.random() * pairs.length)];
  return tiles.map((tile) => {
    if (tile.id === first.id) return { ...tile, row: second.row, col: second.col };
    if (tile.id === second.id) return { ...tile, row: first.row, col: first.col };
    return tile;
  });
};

export const maxTileValue = (tiles) =>
  tiles.reduce((max, tile) => {
    if (tile.type === BLOCK_TYPE) return max;
    return Math.max(max, tile.value);
  }, 0);
